import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import { FileText, ExternalLink, FolderOpen, ChevronDown, ChevronRight, Folder, Loader2 } from 'lucide-react';

export default function PolicyHub({ isAdmin = false }) {
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [loadingFile, setLoadingFile] = useState(null);

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      const response = await api.get('/storage/policies');
      let data = response.data;
      
      // For non-admin users, only show Policies & Procedures folder
      if (!isAdmin) {
        data = data.map(folder => {
          if (folder.name === 'Administrative') {
            // Find the "Policies & Procedures" subfolder
            const policiesFolder = folder.subfolders?.find(
              sub => sub.name === 'Policies & Procedures'
            );
            
            if (policiesFolder) {
              // Return only the Policies & Procedures content
              return {
                name: 'Policies & Procedures',
                path: policiesFolder.path,
                files: policiesFolder.files || [],
                subfolders: policiesFolder.subfolders || []
              };
            }
          }
          return null;
        }).filter(Boolean);
      }
      
      setFolders(data);
      
      // Auto-expand top-level folders
      const expanded = {};
      data.forEach(folder => {
        expanded[folder.path] = true;
      });
      setExpandedFolders(expanded);
    } catch (error) {
      console.error('Failed to load policies:', error);
      toast.error('Failed to load policies from storage');
    } finally {
      setLoading(false);
    }
  };

  const toggleFolder = (folderPath) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderPath]: !prev[folderPath]
    }));
  };

  const formatFileName = (fileName) => {
    // Remove file extension and clean up the name
    const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
    // Replace underscores and hyphens with spaces
    return nameWithoutExt.replace(/[_-]/g, ' ');
  };

  const getFileExtension = (fileName) => {
    const ext = fileName.split('.').pop().toUpperCase();
    return ext;
  };

  const countAllFiles = (folder) => {
    let count = folder.files?.length || 0;
    if (folder.subfolders) {
      folder.subfolders.forEach(sub => {
        count += countAllFiles(sub);
      });
    }
    return count;
  };

  const handleViewFile = async (file) => {
    setLoadingFile(file.path);
    try {
      const response = await api.get(`/storage/file-url?path=${encodeURIComponent(file.path)}`);
      const url = response.data.url;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Failed to get file URL:', error);
      toast.error('Failed to open file. Please try again.');
    } finally {
      setLoadingFile(null);
    }
  };

  const totalFiles = folders.reduce((acc, folder) => acc + countAllFiles(folder), 0);

  // Recursive folder renderer
  const renderFolder = (folder, depth = 0) => {
    const isExpanded = expandedFolders[folder.path];
    const fileCount = countAllFiles(folder);
    const hasContent = fileCount > 0;
    
    if (!hasContent) return null;
    
    return (
      <div key={folder.path} className={depth > 0 ? 'ml-4 border-l-2 border-purple-100 pl-4' : ''}>
        {/* Folder Header */}
        <button
          onClick={() => toggleFolder(folder.path)}
          className={`w-full p-3 flex items-center gap-3 hover:bg-purple-50 transition-colors text-left rounded-lg ${
            depth === 0 ? 'bg-purple-50/50' : ''
          }`}
          data-testid={`folder-toggle-${folder.path.replace(/[\s\/]/g, '-').toLowerCase()}`}
        >
          <div className={`p-2 rounded-lg ${depth === 0 ? 'bg-purple-100' : 'bg-purple-50'}`}>
            {depth === 0 ? (
              <FolderOpen className="w-4 h-4 text-purple-600" />
            ) : (
              <Folder className="w-4 h-4 text-purple-500" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className={`font-semibold truncate ${depth === 0 ? 'text-purple-900' : 'text-purple-800 text-sm'}`}>
              {folder.name}
            </h3>
            <p className="text-xs text-gray-500">
              {fileCount} document{fileCount !== 1 ? 's' : ''}
              {folder.subfolders?.length > 0 && ` • ${folder.subfolders.length} subfolder${folder.subfolders.length !== 1 ? 's' : ''}`}
            </p>
          </div>
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
          )}
        </button>

        {/* Folder Contents */}
        {isExpanded && (
          <div className="mt-1">
            {/* Files in this folder */}
            {folder.files && folder.files.length > 0 && (
              <div className="space-y-1 mb-2">
                {folder.files.map((file, index) => (
                  <div
                    key={file.id || index}
                    className="p-3 ml-2 hover:bg-purple-50/50 transition-colors rounded-lg flex items-center gap-3"
                    data-testid={`file-item-${file.name}`}
                  >
                    <div className="p-1.5 bg-white border border-purple-100 rounded flex-shrink-0">
                      <FileText className="w-4 h-4 text-purple-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {formatFileName(file.name)}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                          {getFileExtension(file.name)}
                        </span>
                        {file.size && (
                          <span className="text-xs text-gray-500">
                            {file.size > 1024 * 1024 
                              ? `${(file.size / (1024 * 1024)).toFixed(1)} MB`
                              : `${(file.size / 1024).toFixed(1)} KB`}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => handleViewFile(file)}
                      disabled={loadingFile === file.path}
                      className="bg-purple-600 hover:bg-purple-700 text-white flex-shrink-0"
                      size="sm"
                      data-testid={`view-file-${file.name}`}
                    >
                      {loadingFile === file.path ? (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      ) : (
                        <ExternalLink className="w-3 h-3 mr-1" />
                      )}
                      View
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Subfolders */}
            {folder.subfolders && folder.subfolders.length > 0 && (
              <div className="space-y-1">
                {folder.subfolders.map(subfolder => renderFolder(subfolder, depth + 1))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (folders.length === 0) {
    return (
      <Card className="p-12 text-center border-purple-100">
        <div className="flex justify-center mb-4">
          <div className="p-4 bg-purple-50 rounded-full">
            <FolderOpen className="w-12 h-12 text-purple-400" />
          </div>
        </div>
        <h3 className="text-lg font-semibold text-purple-900 mb-2">No policies available</h3>
        <p className="text-gray-600">
          {isAdmin 
            ? 'Upload policy documents to your Supabase storage bucket to see them here.'
            : 'Check back soon for important company documents.'}
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <Card className="p-4 bg-gradient-to-r from-purple-50 to-white border-purple-100">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-purple-100 rounded-xl">
            <FileText className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <p className="text-sm text-gray-600">Total Documents</p>
            <p className="text-2xl font-bold text-purple-900">{totalFiles}</p>
          </div>
          <div className="ml-auto text-right">
            <p className="text-sm text-gray-600">Categories</p>
            <p className="text-2xl font-bold text-purple-900">{folders.length}</p>
          </div>
        </div>
      </Card>

      {/* Folders with Files */}
      <Card className="border-purple-100 p-4">
        <div className="space-y-2">
          {folders.map(folder => renderFolder(folder))}
        </div>
      </Card>
    </div>
  );
}
